//#region IMPORTS
import {
  fetchShippableOrdersAsync,
  fetchApprovableOrdersAsync,
  fetchInProgressOrdersAsync,
} from "../orders/ordersSlice";
import {
  fetchApprovableInstoreOrdersAsync,
  fetchShippableInstoreOrdersAsync,
  fetchInProgressInstoreOrdersAsync,
  fetchInstoreFlowCountsAsync,
} from "../instoreOrders/instoreOrdersSlice";
import { useAppDispatch, useAppSelector } from "../../store/configureStore";
import { useEffect, useRef, useState } from "react";
import { fetchBasketReasonsAsync } from "../basketReason/basketReasonSlice";
import { Redirect, Route, Switch } from "react-router-dom";
import { fetchPromosAsync } from "../promos/promosSlice";
import { useTranslation } from "react-i18next";
import { closeDash } from "./dashboardSlice";
import { toast } from "react-toastify";
import UserInstoreOrderDetailPage from "../instoreOrders/UserInstoreOrderDetailPage";
import InstoreTemplateDetailPage from "../instoreTemplates/InstoreTemplateDetailPage";
import ProductVersionsDetailPage from "../productVersions/ProductVersionsDetailPage";
import InstoreOrderDetailPage from "../instoreOrders/InstoreOrderDetailPage";
import TranslationManagerPage from "../translationManager/TranslationManagerPage";
import InstoreOrderTypesPage from "../instoreOrderTypes/InstoreOrderTypesPage";
import InstoreTemplatesPage from "../instoreTemplates/InstoreTemplatesPage";
import InstoreBasketConfirm from "../instoreBasket/InstoreBasketConfirm";
import ProductVersionsPage from "../productVersions/ProductVersionsPage";
import ProductsDetailPage from "../products/ProductsDetailPage";
import DashboardContainer from "../../components/dashboard/DashboardContainer";
import InstoreBasketPage from "../instoreBasket/InstoreBasketPage";
import InstoreThemesPage from "../instoreThemes/InstoreThemesPage";
import OrdersHistoryPage from "../orders/OrdersHistoryPage";
import DashboardMessage from "../../components/dashboard/DashboardMessage";
import InstoreTypesPage from "../instoreTypes/InstoreTypesPage";
import usePickerManager from "../../hooks/usePickerManager";
import CheckoutConfirm from "../checkout/CheckoutConfirm";
import useAdminManager from "../../hooks/useAdminManager";
import OrderDetailPage from "../orders/OrderDetailPage";
import BackOrderPage from "../backorder/BackOrderPage";
import WeekOrderPage from "../weekOrder/WeekOrderPage";
import ItemBigImage from "../../components/itemsGrid/ItemBigImage";
import CheckoutPage from "../checkout/CheckoutPage";
import OrderRulesPage from "../orderRules/OrderRulesPage";
import SettingsPage from "../settings/SettingsPage";
import ProductsPage from "../products/ProductsPage";
import useHQmanager from "../../hooks/useHQmanager";
import StoreDetails from "../stores/StoreDetails";
import FeaturePage from "../faq/FeaturePage";
import StoresPage from "../stores/StoresPage";
import BasketPage from "../basket/BasketPage";
import PromosPage from "../promos/PromosPage";
import AdminRoute from "../../components/routes/AdminRoute";
import StoreRoute from "../../components/routes/StoreRoute";
import UsersPage from "../users/UsersPage";
import HqRoute from "../../components/routes/HqRoute";
import Home from "../home/Home";
//#endregion

export default function DashboardV2() {
  //#region SETUP
  // Ref
  const toastId: any = useRef();

  // Translation
  const { t } = useTranslation();

  // Redux
  const dispatch = useAppDispatch();
  const { promosLoaded } = useAppSelector((state) => state.promos);

  // Local state
  const [messageShown, setMessageShown] = useState<boolean>(false);

  // Roles
  const { user } = useAppSelector((state) => state.account);
  const { refreshingTransmart } = useAppSelector((state) => state.promos);
  const isPicker = usePickerManager(user);
  const isAdmin = useAdminManager(user);
  const isHq = useHQmanager(user);

  // Check device
  const windowSize = useRef([window.innerWidth, window.innerHeight]);

  //#endregion

  //#region LOGIC
  // Close Dashboard on smaller screens
  useEffect(() => {
    if (windowSize.current[0] < 996) {
      dispatch(closeDash());
    }
  }, [dispatch]);

  // Load promos
  useEffect(() => {
    if (!promosLoaded) {
      dispatch(fetchPromosAsync());
    }
  }, [promosLoaded, dispatch]);

  // Load basket reasons
  useEffect(() => {
    dispatch(fetchBasketReasonsAsync());
  }, [dispatch]);

  // Load role specific orders
  useEffect(() => {
    const fetchRoleSpecificOrders = async () => {
      // Promo approve is HQ / admin only — pickers don't need the pending count.
      (isAdmin || isHq) && (await dispatch(fetchApprovableOrdersAsync()));
      (isAdmin || isPicker) && (await dispatch(fetchShippableOrdersAsync()));
      (isAdmin || isPicker) &&
        (await dispatch(fetchInProgressOrdersAsync()));
      (isAdmin || isHq || isPicker) &&
        (await dispatch(fetchApprovableInstoreOrdersAsync()));
      (isAdmin || isPicker) &&
        (await dispatch(fetchShippableInstoreOrdersAsync()));
      (isAdmin || isPicker) &&
        (await dispatch(fetchInProgressInstoreOrdersAsync()));
      // Per-type split counts for the instore tab badge + flow pills.
      (isAdmin || isPicker) && (await dispatch(fetchInstoreFlowCountsAsync()));
    };
    fetchRoleSpecificOrders();
  }, [dispatch, isAdmin, isHq, isPicker]);

  // ShowUpdating badge
  useEffect(() => {
    if (refreshingTransmart) {
      toastId.current = toast("Updating transmart connection", {
        autoClose: false,
        theme: "dark",
        type: "info",
        closeOnClick: false,
      });
    } else {
      toast.update(toastId.current, {
        render: "Update succesfull",
        type: "success",
        autoClose: 5000,
      });
    }
  }, [refreshingTransmart]);
  //#endregion

  //#region RENDER
  return (
    <DashboardContainer>
      <Switch>
        {/* SETTINGS */}
        <AdminRoute exact path="/settings" component={SettingsPage} />

        {/* USERS */}
        <AdminRoute exact path="/users" component={UsersPage} />

        {/* TRANSLATIONMANAGER */}
        <AdminRoute
          exact
          path="/translations"
          component={TranslationManagerPage}
        />

        {/* PRODUCTVERSIONS */}
        <AdminRoute
          path="/productversions/:id"
          component={ProductVersionsDetailPage}
        />
        <AdminRoute
          exact
          path="/productversions"
          component={ProductVersionsPage}
        />

        {/* PRODUCTS */}
        <AdminRoute path="/products/:id" component={ProductsDetailPage} />
        <AdminRoute exact path="/products" component={ProductsPage} />

        {/* STORES */}
        <StoreRoute exact path="/stores/manage" component={StoreDetails} />
        <StoreRoute path="/stores/manage/:id" component={StoreDetails} />
        <StoreRoute exact path="/stores" component={StoresPage} />

        {/* PROMOS */}
        <Route exact path="/promos" component={PromosPage} />
        <Route
          exact
          path="/promos/instore/:templateId"
          component={WeekOrderPage}
        />

        {/* INSTORE ORDERS */}
        <HqRoute path="/instoreorders/create">
          <InstoreOrderDetailPage create />
        </HqRoute>
        <AdminRoute
          path="/instoreorders/edit/:id"
          component={InstoreOrderDetailPage}
        />
        <Route path="/instoreorders/:id">
          {!isAdmin && !isPicker && !isHq ? (
            <UserInstoreOrderDetailPage />
          ) : (
            <InstoreOrderDetailPage readonly />
          )}
        </Route>

        {/* INSTORE TEMPLATES */}
        <HqRoute path="/instoretemplates/:id">
          <InstoreTemplateDetailPage />
        </HqRoute>
        <HqRoute path="/instoretemplates">
          <InstoreTemplatesPage />
        </HqRoute>

        {/* INSTORE THEMES */}
        <HqRoute path="/instorethemes">
          <InstoreThemesPage />
        </HqRoute>

        {/* INSTORE TYPES */}
        <HqRoute path="/instoretypes">
          <InstoreTypesPage />
        </HqRoute>

        {/* INSTORE ORDER TYPES */}
        <HqRoute path="/instoreordertypes">
          <InstoreOrderTypesPage />
        </HqRoute>

        {/* CHECKOUT */}
        <Route path="/checkout/:id" component={CheckoutConfirm} />
        <Route path="/checkout" component={CheckoutPage} />

        {/* BASKET */}
        <Route path="/basket" component={BasketPage} />
        <Route
          exact
          path="/instore-basket/confirm"
          component={InstoreBasketConfirm}
        />
        <Route path="/instore-basket" component={InstoreBasketPage} />

        {/* ALL ORDERS WITH SUBORDERS */}
        <Route path="/orders/:id" component={OrderDetailPage} />
        <Route path="/orders" exact>
          <OrdersHistoryPage
            title={
              !isAdmin && !isPicker && !isHq
                ? t("rituals.orderHistory")
                : t("rituals.allOrders")
            }
          />
        </Route>

        {/* FEATURE PAGE */}
        <Route path="/featurelist" exact component={FeaturePage} />

        {/* ORDER BULK-ACTION RULES (admin reference) */}
        <AdminRoute exact path="/order-rules" component={OrderRulesPage} />

        {/* BACKORDER */}
        <Route path="/backorder/promo/:id" component={BackOrderPage} />

        {/* BIGIMAGE */}
        <Route path="/image/:id" component={ItemBigImage} />

        {/* DEFAULT */}
        <Route path="/" exact component={Home} />
        <Redirect to="/not-found" />
      </Switch>
      <DashboardMessage
        closeHandler={() => setMessageShown(true)}
        hide={messageShown || isAdmin || isPicker}
      />
    </DashboardContainer>
  );
  //#endregion
}
